// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { createBrowserRouter, Outlet, type RouteObject } from 'react-router';
import { SilverAlphaDocuments, SilverAlphaHome } from '@client/silver-alpha';
import {
  Account,
  Advisors,
  App,
  BankAccounts,
  Documents,
  IliquidAssets,
  ManageAccess,
  NotFound,
  SilverAlpha,
} from '@client-app/pages';
import { Layout } from '@client-app/features/ui/layout';
import {
  AuthenticatedLayout,
  OAuthCallback as Auth0OAuthCallback,
  POST_LOGIN_REDIRECT_TOKENS,
} from '@shared/auth';
import { dashboardLoader } from '@client-app/features/utils/dashboard-loader';
import { silverAlphaLoader } from '@client-app/features/utils/silver-alpha-loader';
import { Layout as AggregationLayout } from '@shared/advisory/aggregation/layouts';
import {
  Summary,
  InvestmentOverview,
  Loans,
  Cashflow,
  Transactions,
  OAuthCallback,
} from '@shared/advisory/aggregation/components';
import { InstitutionSyncProvider } from '@shared/advisory/aggregation/providers';
import { TagManagementProvider } from '@shared/tag-management';
import { isBuildDevelopment } from '@shared/environment';

type AppRouteObject =
  | false
  | RouteObject
  | (RouteObject & {
      children?: Array<AppRouteObject>;
    });

const cleanRoutes = (routes: Array<AppRouteObject>): RouteObject[] => {
  return routes
    .filter((route): route is Exclude<AppRouteObject, false> => Boolean(route))
    .map(route => {
      if (route.children) {
        const cleanedChildren = cleanRoutes(route.children);

        return {
          ...route,
          children: cleanedChildren.length ? cleanedChildren : undefined,
        };
      }

      return route;
    });
};

export const routes: Array<AppRouteObject> = [
  {
    path: '/',
    element: (
      <AuthenticatedLayout>
        <App />
        <InstitutionSyncProvider>
          <TagManagementProvider>
            <Outlet />
          </TagManagementProvider>
        </InstitutionSyncProvider>
      </AuthenticatedLayout>
    ),
    errorElement: <NotFound error />,
    children: [
      {
        element: <Layout />,
        children: [
          {
            path: '',
            index: true,
            element: <span />,
            loader: dashboardLoader,
            hydrateFallbackElement: <div />,
          },
          {
            path: 'aggregation',
            hydrateFallbackElement: <div />,
            loader: dashboardLoader,
            element: (
              <AggregationLayout excludedPaths={['/aggregation/illiquids']}>
                <Outlet />
              </AggregationLayout>
            ),
            children: [
              {
                element: <Summary />,
                index: true,
              },
              {
                element: <InvestmentOverview />,
                path: 'investment',
              },
              {
                element: <IliquidAssets />,
                path: 'illiquids',
              },
              {
                element: <Loans />,
                path: 'loans',
              },
              {
                element: <Cashflow />,
                path: 'cashflow',
              },
              {
                element: <Transactions />,
                path: 'transactions',
              },
            ],
          },
          {
            path: 'account',
            element: <Account />,
          },
          {
            path: 'banks',
            element: <BankAccounts />,
          },
          {
            path: 'documents',
            element: <Documents />,
          },
          isBuildDevelopment
            ? {
                path: 'manage-access',
                element: <ManageAccess />,
              }
            : {
                path: 'advisors',
                element: <Advisors />,
              },
          {
            path: 'callback/:provider',
            element: <OAuthCallback />,
          },
        ],
      },
      {
        path: 'silver-alpha',
        element: <SilverAlpha />,
        loader: silverAlphaLoader,
        hydrateFallbackElement: <div />,
        children: [
          {
            index: true,
            element: <SilverAlphaHome />,
          },
          {
            path: 'documents',
            element: <SilverAlphaDocuments />,
          },
        ],
      },
    ],
  },
  {
    path: 'callback',
    element: (
      <Auth0OAuthCallback
        redirectTo="/"
        postLoginRedirectTokens={POST_LOGIN_REDIRECT_TOKENS}
      />
    ),
  },
  {
    path: '*',
    errorElement: <NotFound error />,
    element: <NotFound />,
  },
];

export const router = createBrowserRouter(cleanRoutes(routes));
